import type { LoaderFunctionArgs, MetaFunction } from '@remix-run/node'
import { prisma } from '#app/utils/db.server.ts'
import { json } from '@remix-run/node'
import { Link, redirect, useLoaderData } from '@remix-run/react'
import { invariant } from '@epic-web/invariant'
import ProductImages from './components/product-images.tsx'
import ProductDetails from './components/product-details.tsx'
import RelatedProducts from './components/related-products.tsx'
import { stripHtml } from '#app/utils/misc.tsx'
import { Prisma } from '@prisma/client'
import IndoorImage from '#app/images/og-hydrofarm-indoor-flower.jpg'
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '#app/components/ui/breadcrumb.tsx'

export const meta: MetaFunction<typeof loader> = ({
	data,
	matches,
	location,
}) => {
	const rootData = matches.find((match) => match.id === 'root')?.data as {
		generalSettings?: {
			website_name: string
			twitter_site: string
			twitter_creator: string
		}
		requestInfo?: { origin: string }
	}

	// Fallback chain for title and description
	const title =
		data?.product?.leafProductMetadata?.title || // First choice: SEO title
		data?.product?.display_name || // Second choice: display name
		data?.product?.name || // Third choice: regular name
		'Product' // Final fallback

	const description =
		data?.product?.leafProductMetadata?.description || // First choice: SEO description
		stripHtml(data?.product?.description) || // Second choice: product description
		data?.product?.tagline || // Third choice: product tagline
		'Product description' // Final fallback

	const fullUrl = location.search
		? `${rootData?.requestInfo?.origin}${location.pathname + location.search}`
		: `${rootData?.requestInfo?.origin}${location.pathname}`

	return [
		{ title: `${title} | ${rootData?.generalSettings?.website_name}` },
		{ name: 'description', content: description },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:url', content: fullUrl },
		{ property: 'og:type', content: 'product' },
		{ property: 'twitter:card', content: 'summary_large_image' },
		{ property: 'twitter:title', content: title },
		{ property: 'twitter:description', content: description },

		// Conditionally add image meta tags
		...(data?.product?.images?.[0]?.image
			? [
					{ property: 'og:image', content: data.product.images[0].image },
					{ property: 'twitter:image', content: data.product.images[0].image },
				]
			: []),

		// Conditional Twitter tags
		...(rootData?.generalSettings?.twitter_site
			? [
					{
						name: 'twitter:site',
						content: rootData.generalSettings.twitter_site,
					},
				]
			: []),
		...(rootData?.generalSettings?.twitter_creator
			? [
					{
						name: 'twitter:creator',
						content: rootData.generalSettings.twitter_creator,
					},
				]
			: []),
	]
}

// Type the parameters for better type safety
interface WhereClause {
	OR: Prisma.LeafProductWhereInput[]
	AND?: Prisma.LeafProductWhereInput[]
}

export async function loader({ request, params }: LoaderFunctionArgs) {
	const { slug, location } = params
	const url = new URL(request.url)
	invariant(slug, 'Missing Product ID')

	// Check number of companies
	const companyCount = await prisma.leafCompany.count()

	// If multiple companies exist but no location specified, redirect to state selection
	// ELSE if single company with location specified, redirect to canonical URL
	if (companyCount > 1 && !location) {
		// Multiple companies but no location - redirect to state selection
		return redirect(`/`)
		// TODO:
		// return redirect(`/select-location?redirect=/products/${slug}${url.search}`)
	} else if (companyCount === 1 && location) {
		// Single company with location - redirect to canonical URL
		return redirect(`/products/${slug}${url.search}`)
	}

	const where: WhereClause = {
		OR: [
			{ leafProductMetadata: { slug: params.slug } },
			{ internalId: params.slug },
		],
	}

	// Add state filter if location exists
	if (location) {
		where.AND = [
			{
				leafSeller: {
					state: {
						equals: location.toUpperCase(),
						mode: 'insensitive',
					},
				},
			},
		]
	}

	//console.log(params)

	const product = await prisma.leafProduct.findFirst({
		where,
		select: {
			internalId: true,
			id: true,
			name: true,
			display_name: true,
			images: true,
			strains: true,
			description: true,
			product_data_items: true,
			tagline: true,
			parent: true,
			leafProductCategory: {
				select: {
					name: true,
					slug: true,
				},
			},
			leafProductSubcategory: {
				select: {
					name: true,
					slug: true,
				},
			},
			leafProductMetadata: {
				select: {
					slug: true,
					title: true,
					description: true,
				},
			},
		},
	})

	invariant(product, 'Product not found')

	// get all children products
	const children = await prisma.leafProduct.findMany({
		where: {
			parent: {
				path: ['id'],
				equals: product.id,
			},
		},
		select: {
			id: true,
			name: true,
			display_name: true,
			listing_state: true,
			images: true,
			created_on: true,
			leafProductCategory: {
				select: { name: true },
			},
			leafProductLine: {
				select: { name: true },
			},
			leafSeller: {
				select: {
					state: true,
					slug: true,
				},
			},
		},
		orderBy: {
			created_on: 'desc',
		},
	})

	// Since 'parent' is a JSON field, we need to fetch parent images separately
	// as Prisma doesn't support nested selects on JSON fields
	if (product.parent && (product.parent as { id: number }).id) {
		const parentImage = await prisma.leafProduct.findUnique({
			where: {
				id: (product.parent as { id: number }).id,
			},
			select: {
				id: true,
				images: {
					select: {
						id: true,
						image: true,
					},
				},
			},
		})

		if (parentImage) {
			;(
				product as typeof product & {
					parentImages: typeof parentImage.images
				}
			).parentImages = parentImage.images
		}
	}

	// Get related products
	const relatedProducts = await prisma.leafProduct.findMany({
		where: {
			parent: {
				equals: Prisma.JsonNull,
			},
			AND: [
				{
					OR: [
						{
							leafProductCategory: {
								slug: product.leafProductCategory?.slug,
							},
						},
						{
							leafProductSubcategory: {
								slug: product.leafProductSubcategory?.slug,
							},
						},
						{
							strains: {
								hasSome: product.strains ?? [],
							},
						},
					],
				},
				{
					internalId: {
						not: product.internalId,
					},
				},
				{
					leafProductMetadata: {
						show_on_website: true,
					},
				},

				...(location
					? [
							{
								leafSeller: {
									state: {
										equals: location.toUpperCase(),
									},
								},
							},
						]
					: []),
			],
		},
		select: {
			parent: true,
			internalId: true,
			id: true,
			name: true,
			display_name: true,
			images: true,
			strains: true,
			leafProductCategory: {
				select: {
					name: true,
					slug: true,
				},
			},
			leafProductSubcategory: {
				select: {
					name: true,
					slug: true,
				},
			},
			leafProductMetadata: {
				select: {
					slug: true,
					title: true,
					description: true,
				},
			},
			product_data_items: true,
		},
		take: 12,
	})

	const shuffledProducts = [...relatedProducts]
		.sort(() => Math.random() - 0.5)
		.slice(0, 3)

	// Since 'parent' is a JSON field, we need to fetch parent images separately
	// as Prisma doesn't support nested selects on JSON fields
	const relatedParentIds = [
		...new Set(
			shuffledProducts
				.filter(
					(product) => product.parent && (product.parent as { id: number }).id,
				)
				.map((product) => (product.parent as { id: number }).id),
		),
	]

	if (relatedParentIds.length > 0) {
		const parentImages = await prisma.leafProduct.findMany({
			where: {
				id: {
					in: relatedParentIds,
				},
			},
			select: {
				id: true,
				images: {
					select: {
						id: true,
						image: true,
					},
				},
			},
		})

		shuffledProducts.forEach((product) => {
			if (product.parent && (product.parent as { id: number }).id) {
				const parentImage = parentImages.find(
					(p) => p.id === (product.parent as { id: number }).id,
				)
				if (parentImage) {
					;(
						product as typeof product & {
							parentImages: typeof parentImage.images
						}
					).parentImages = parentImage.images
				}
			}
		})
	}

	return json({ product, children, relatedProducts: shuffledProducts })
}

export default function Product() {
	const { product, children, relatedProducts } = useLoaderData<typeof loader>()

	//console.log(product)
	//console.log(relatedProducts)
	//console.log(children)

	return (
		<>
			<div className="bg-dark-main relative overflow-hidden pb-20 pt-28 text-white lg:pb-24 lg:pt-32 xl:pt-40">
				<div className="container relative z-10">
					<div className="mb-6 space-y-1">
						<h2 className="text-balance text-3xl font-extrabold tracking-tight lg:text-4xl">
							Product Details
						</h2>
						<Breadcrumb>
							<BreadcrumbList className="text-white/80">
								<BreadcrumbItem>
									<BreadcrumbLink
										className="hover:text-theme-blue hover:underline"
										asChild
									>
										<Link to="/">Home</Link>
									</BreadcrumbLink>
								</BreadcrumbItem>
								<BreadcrumbSeparator />
								<BreadcrumbItem>
									<BreadcrumbLink
										className="hover:text-theme-blue hover:underline"
										asChild
									>
										<Link to="/products">Products</Link>
									</BreadcrumbLink>
								</BreadcrumbItem>
								<BreadcrumbSeparator />
								<BreadcrumbItem>
									<BreadcrumbPage className="text-theme-blue">
										{product.name}
									</BreadcrumbPage>
								</BreadcrumbItem>
							</BreadcrumbList>
						</Breadcrumb>
					</div>
				</div>

				<div className="absolute inset-0 z-0">
					<img
						className="h-full w-full object-cover"
						src={IndoorImage}
						alt="Indoor Cannabis Hydrofarm"
					/>
					<div className="absolute inset-0 bg-gradient-to-r from-black to-[#80caff] mix-blend-multiply"></div>
				</div>
			</div>

			<div className="container pb-32 pt-10">
				<div className="mb-20 grid grid-cols-1 gap-8 lg:grid-cols-12">
					<ProductImages />
					<ProductDetails />
				</div>
				<RelatedProducts />
			</div>
		</>
	)
}
